import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { loadXlsx } from '../../shared/utils/loadXlsx'
import {
  getPartCatalog,
  importPartCatalog,
  type ImportCatalogResult,
  type PartCatalogEntry,
} from '../../shared/api/partCatalog.api'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'

interface ParsedRow {
  partNumber: string
  brand: string
}

async function parseExcel(file: File): Promise<ParsedRow[]> {
  // Либу подтягиваем ДО new Promise: иначе колбэк reader.onload пришлось бы
  // делать async и отказ загрузки чанка потерялся бы мимо reject.
  const XLSX = await loadXlsx()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: 'array' })
        const sheet = wb.Sheets[wb.SheetNames[0]!]
        if (!sheet) {
          resolve([])
          return
        }
        const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' })
        // Пытаемся найти колонки по частичному совпадению заголовков.
        const first = raw[0] ?? {}
        const keys = Object.keys(first)
        const pnKey =
          keys.find((k) => /part\s*number|партномер|арт(икул)?/i.test(k)) ?? keys[0] ?? 'partNumber'
        const brKey = keys.find((k) => /brand|бренд|производитель/i.test(k)) ?? keys[1] ?? 'brand'
        const rows = raw
          .map((r) => ({
            partNumber: String(r[pnKey] ?? '').trim(),
            brand: String(r[brKey] ?? '').trim(),
          }))
          .filter((r) => r.partNumber.length > 0 && r.brand.length > 0)
        resolve(rows)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.readAsArrayBuffer(file)
  })
}

export function PartCatalogPage() {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [parsedRows, setParsedRows] = useState<ParsedRow[] | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<ImportCatalogResult | null>(null)
  const [search, setSearch] = useState('')

  const {
    data: catalogResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['part-catalog', search],
    queryFn: () => getPartCatalog(search || undefined),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })

  const items = catalogResult?.items ?? []
  const total = catalogResult?.total ?? 0

  const importMut = useMutation({
    mutationFn: importPartCatalog,
    onSuccess: (res) => {
      setImportResult(res)
      setParsedRows(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      void queryClient.invalidateQueries({ queryKey: ['part-catalog'] })
    },
  })

  const handleFile = async (file: File) => {
    setParseError(null)
    setImportResult(null)
    try {
      const rows = await parseExcel(file)
      if (rows.length === 0) {
        setParseError('В файле не найдено валидных строк. Нужны колонки Part Number и Brand.')
        setParsedRows(null)
        return
      }
      setParsedRows(rows)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Ошибка парсинга Excel')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold text-gray-900">Каталог парт-номеров</h1>
        <span className="text-sm text-gray-400">{total.toLocaleString('ru-RU')} записей</span>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFile(file)
            }}
            className="text-sm"
          />
          {parsedRows && (
            <button
              onClick={() => importMut.mutate(parsedRows)}
              disabled={importMut.isPending}
              className="px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {importMut.isPending
                ? 'Импорт…'
                : `Импортировать ${parsedRows.length.toLocaleString('ru-RU')} строк`}
            </button>
          )}
        </div>

        {parseError && <p className="text-sm text-red-600">{parseError}</p>}

        {parsedRows && (
          <div className="text-xs text-gray-500">
            Предпросмотр первых 5 строк:
            <div className="mt-1 divide-y divide-gray-100 rounded border border-gray-100 bg-gray-50">
              {parsedRows.slice(0, 5).map((r, i) => (
                <div key={i} className="flex gap-3 px-2 py-1">
                  <span className="font-mono text-gray-800 w-48 truncate">{r.partNumber}</span>
                  <span className="text-gray-600">{r.brand}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {importResult && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <div className="font-medium text-emerald-900">Импорт завершён</div>
            <div className="mt-1 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-emerald-800">
              <span>
                Всего: <b className="tabular-nums">{importResult.total}</b>
              </span>
              <span>
                Добавлено: <b className="tabular-nums">{importResult.inserted}</b>
              </span>
              <span>
                Обновлено: <b className="tabular-nums">{importResult.updated}</b>
              </span>
              <span>
                Пропущено: <b className="tabular-nums">{importResult.skipped}</b>
              </span>
              <span className={importResult.errors > 0 ? 'text-red-700' : ''}>
                Ошибок: <b className="tabular-nums">{importResult.errors}</b>
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm text-gray-500">Каталог</div>
          <input
            type="text"
            placeholder="Поиск по парт-номеру или бренду…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 w-72"
          />
        </div>

        {isLoading && <PageLoader />}
        {error && <ErrorMessage error={error} />}
        {!isLoading && (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-xs text-gray-500">
                  <th className="text-left font-normal pb-2">Парт-номер</th>
                  <th className="text-left font-normal pb-2">Бренд</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e: PartCatalogEntry) => (
                  <tr key={e.id} className="border-t border-gray-100">
                    <td className="py-1.5 font-mono text-xs text-gray-900">{e.partNumber}</td>
                    <td className="py-1.5 text-gray-700">{e.brand}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && search && (
              <p className="text-sm text-gray-400 py-3 text-center">Не найдено</p>
            )}
            {total > items.length && !search && (
              <p className="text-xs text-gray-400 py-2 text-center">
                Показано первых {items.length.toLocaleString('ru-RU')} из{' '}
                {total.toLocaleString('ru-RU')} — используйте поиск для точного поиска.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
